import { CircleCheck, TriangleAlert, Upload, XCircle } from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@components/ui/alert';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { Checkbox } from '@components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@components/ui/dialog';
import { Label } from '@components/ui/label';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@components/ui/table';
import { TextField } from '../../../components/textfield';
import { Api } from '../../../core/api';
import { injectTSDI } from '../../../core/tsdi';
import {
    BulkAccountCorrectionResponse,
    CorrectionResult
} from '../../../core/api/WalletClient';

interface BulkCorrectionModalProps {
    open?: boolean;
    onModalClose?(): void;
    onSuccess?(): void;
}

export const BulkCorrectionModal = ({
    open: initialOpen = false,
    onModalClose,
    onSuccess
}: BulkCorrectionModalProps) => {
    const [open, setOpen] = useState(initialOpen);
    const { walletClient, googleUserAuth } = injectTSDI(Api);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [tenantId, setTenantId] = useState('');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [consentChecked, setConsentChecked] = useState(false);
    const [response, setResponse] =
        useState<BulkAccountCorrectionResponse | null>(null);

    const handleClose = () => {
        setOpen(false);
        onModalClose?.();
        resetForm();
    };

    const resetForm = () => {
        setTenantId('');
        setSelectedFile(null);
        setError(null);
        setSuccess(false);
        setConsentChecked(false);
        setResponse(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    useEffect(() => {
        setOpen(initialOpen);
    }, [initialOpen]);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (!file.name.endsWith('.csv')) {
                setError('Please select a CSV file');
                return;
            }
            setSelectedFile(file);
            setError(null);
        }
    };

    const handleSubmit = async () => {
        setError(null);
        setSuccess(false);
        setResponse(null);

        if (!selectedFile) {
            setError('Please select a CSV file to upload');
            return;
        }

        if (!googleUserAuth?.email) {
            setError('User authentication required');
            return;
        }

        try {
            setLoading(true);

            const result =
                await walletClient.supportApi.walletSupportControllerCreateBulkCorrection(
                    {
                        createdBy: googleUserAuth.email,
                        ...(tenantId && { tenantId: tenantId.trim() })
                    },
                    {
                        file: selectedFile
                    }
                );

            if (result.ok) {
                setResponse(result.data);
                setSuccess(true);
                onSuccess?.();
            } else {
                throw new Error('Failed to process bulk correction');
            }
        } catch (err: any) {
            setError(err?.message || 'Failed to process bulk correction');
        } finally {
            setLoading(false);
        }
    };

    const renderResults = () => {
        if (!response) return null;

        const hasFailures = response.failed > 0;

        return (
            <div className="mt-4">
                <div className="mb-4 flex gap-2">
                    <Badge variant="success">
                        <CircleCheck />
                        Processed: {response.processed}
                    </Badge>
                    {hasFailures && (
                        <Badge variant="destructive">
                            <XCircle />
                            Failed: {response.failed}
                        </Badge>
                    )}
                </div>

                {response.results && response.results.length > 0 && (
                    <div className="max-h-[300px] overflow-auto rounded-lg border">
                        <Table>
                            <TableHeader className="sticky top-0 bg-card">
                                <TableRow>
                                    <TableHead>User ID</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Value</TableHead>
                                    <TableHead>Currency</TableHead>
                                    <TableHead>Message</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {response.results.map(
                                    (
                                        result: CorrectionResult,
                                        index: number
                                    ) => (
                                        <TableRow key={index}>
                                            <TableCell>
                                                {result.userId}
                                            </TableCell>
                                            <TableCell>
                                                {result.success ? (
                                                    <Badge variant="success">
                                                        Success
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="destructive">
                                                        Failed
                                                    </Badge>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {result.value ?? '-'}
                                            </TableCell>
                                            <TableCell>
                                                {result.currency ?? 'STR'}
                                            </TableCell>
                                            <TableCell>
                                                {result.message || '-'}
                                            </TableCell>
                                        </TableRow>
                                    )
                                )}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </div>
        );
    };

    return (
        <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <TriangleAlert className="size-5 text-warning" />
                        Bulk Correction
                    </DialogTitle>
                </DialogHeader>
                <div className="flex flex-col gap-4">
                    {error && (
                        <Alert variant="destructive">
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}
                    {success && (
                        <Alert variant="success">
                            <AlertDescription>
                                Bulk correction processed successfully!
                            </AlertDescription>
                        </Alert>
                    )}

                    <Alert variant="destructive">
                        <TriangleAlert />
                        <AlertTitle>Critical Financial Operation</AlertTitle>
                        <AlertDescription>
                            This action will modify wallet balances for multiple
                            users. Incorrect data may result in financial
                            discrepancies that are difficult to reverse. Please
                            ensure the CSV file has been thoroughly reviewed
                            before proceeding.
                        </AlertDescription>
                    </Alert>

                    <p className="text-sm text-muted-foreground">
                        Upload a CSV file containing bulk corrections. Each row
                        should specify the user, correction amount, and
                        optionally a currency.
                    </p>

                    <Alert variant="info">
                        <AlertTitle>CSV Format</AlertTitle>
                        <AlertDescription>
                            <ul className="list-disc pl-5">
                                <li>
                                    Delimiter: semicolon (<code>;</code>)
                                </li>
                                <li>
                                    Required columns: <code>user_id</code>,{' '}
                                    <code>Price in STARS</code> (or{' '}
                                    <code>value</code> / <code>amount</code>),{' '}
                                    <code>Comment</code>
                                </li>
                                <li>
                                    Optional column: <code>currency</code>{' '}
                                    (defaults to STR)
                                </li>
                            </ul>
                            <pre className="mt-2 w-full rounded-md bg-muted p-2 font-mono text-xs text-foreground">
                                {`user_id;Price in STARS;Comment;currency\n123;500;First prize;STR\n456;100;EUR bonus;EUR`}
                            </pre>
                        </AlertDescription>
                    </Alert>

                    <div className="rounded-lg border-2 border-dashed p-6 text-center">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv"
                            onChange={handleFileChange}
                            className="hidden"
                            id="bulk-correction-file-input"
                            disabled={loading || success}
                        />
                        <Button
                            variant="outline"
                            disabled={loading || success}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <Upload />
                            {selectedFile ? 'Change File' : 'Select CSV File'}
                        </Button>
                        {selectedFile && (
                            <p className="mt-2 text-sm text-muted-foreground">
                                Selected: <strong>{selectedFile.name}</strong> (
                                {(selectedFile.size / 1024).toFixed(2)} KB)
                            </p>
                        )}
                    </div>

                    <div>
                        <TextField
                            label="Tenant ID (optional)"
                            type="text"
                            value={tenantId}
                            onChange={(value: string) => setTenantId(value)}
                            disabled={loading || success}
                        />
                        <span className="mt-1 block text-xs text-muted-foreground">
                            Leave empty to apply corrections without tenant
                            context
                        </span>
                    </div>

                    {!success && (
                        <div className="rounded-lg bg-warning/15 p-4">
                            <div className="flex items-start gap-3">
                                <Checkbox
                                    id="bulk-correction-consent"
                                    checked={consentChecked}
                                    onCheckedChange={(checked) =>
                                        setConsentChecked(checked === true)
                                    }
                                    disabled={loading}
                                    className="mt-0.5"
                                />
                                <Label
                                    htmlFor="bulk-correction-consent"
                                    className="text-sm font-normal leading-snug"
                                >
                                    I understand that this action will modify
                                    real wallet balances for multiple users and
                                    I accept full responsibility for the
                                    accuracy of the uploaded data.
                                </Label>
                            </div>
                        </div>
                    )}

                    {renderResults()}
                </div>
                <DialogFooter className="gap-2 sm:gap-2">
                    <Button
                        className="flex-1"
                        onClick={handleClose}
                        variant="outline"
                        disabled={loading}
                    >
                        {success ? 'Close' : 'Cancel'}
                    </Button>
                    {!success && (
                        <Button
                            className="flex-1 bg-warning text-warning-foreground hover:bg-warning/90"
                            onClick={handleSubmit}
                            loading={loading}
                            disabled={
                                loading || !consentChecked || !selectedFile
                            }
                        >
                            {loading
                                ? 'Processing...'
                                : 'Submit Bulk Correction'}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
